import { Link, useParams, useSearchParams } from "react-router-dom";
import { useFetch } from "../api";
import { Alerts, Avatar, Badge, EmptyRow, Facts, Loading, Page, Pager, SignedAmount } from "../components/ui";
import { label, money, qs, setQuery, text, when } from "../format";

const ACTIVITY_LIMIT = 20;

function Party({ person }) {
  if (!person || !person.id) return "—";
  return (
    <Link to={"/admin/members/" + person.id} className="user-link">
      <strong>{person.name || "Member"}</strong>
      <span className="code">{person.userId || "—"}</span>
    </Link>
  );
}

function Summary({ title, items }) {
  return (
    <section className="panel">
      <h2 className="section-title">{title}</h2>
      <Facts items={items} />
    </section>
  );
}

export default function MemberActivity() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const result = useFetch("/members/" + id + "/activity" + qs({ page, limit: ACTIVITY_LIMIT }));
  const data = result.data?.data || {};
  const user = data.user;
  const deposits = data.deposits || {};
  const withdrawals = data.withdrawals || {};
  const packages = data.packages || {};
  const transfers = data.transfers || {};
  const packageRows = Array.isArray(packages.rows) ? packages.rows : [];
  const sent = Array.isArray(transfers.sent) ? transfers.sent : [];
  const received = Array.isArray(transfers.received) ? transfers.received : [];
  const activity = Array.isArray(data.activity) ? data.activity : [];

  return (
    <Page title={user ? user.name : "Member activity"} text="Deposits include on-chain credits and approved manual deposits. Withdrawal paid is the amount already sent after the fee. Pending withdrawals are still locked.">
      <Link className="back" to="/admin/members">Back to members</Link>
      <Alerts error={result.error} />
      {result.loading && !user ? <Loading /> : null}
      {user ? (
        <>
          <div className="panel">
            <div className="profile">
              <Avatar src={user.profileImage} name={user.name} />
              <div>
                <h2>{user.name}</h2>
                <p>{user.email}</p>
                <p className="code">{user.userId || user.referralCode}</p>
                <Link to={"/admin/members/" + user.id}>Open profile</Link>
              </div>
            </div>
          </div>

          <div className="balances">
            <div className="balance"><span>Wallet</span><strong>{money(user.balance)}</strong></div>
            <div className="balance"><span>Locked</span><strong>{money(user.lockedBalance)}</strong></div>
          </div>

          <div className="two">
            <Summary
              title="Deposits"
              items={[
                { label: "Credited", value: money(deposits.total) },
                { label: "Credited count", value: deposits.count ?? 0 },
                { label: "Waiting", value: money(deposits.pending) },
                { label: "Waiting count", value: deposits.pendingCount ?? 0 },
              ]}
            />
            <Summary
              title="Withdrawals"
              items={[
                { label: "Sent", value: money(withdrawals.paid) },
                { label: "Sent count", value: withdrawals.paidCount ?? 0 },
                { label: "Still locked", value: money(withdrawals.pending) },
                { label: "Open count", value: withdrawals.pendingCount ?? 0 },
              ]}
            />
            <Summary
              title="Packages"
              items={[
                { label: "Bought", value: packages.count ?? 0 },
                { label: "Active", value: packages.activeCount ?? 0 },
                { label: "Invested", value: money(packages.invested) },
                { label: "ROI credited", value: money(packages.roiCredited) },
                { label: "ROI left", value: money(packages.roiPending) },
              ]}
            />
            <Summary
              title="Transfers"
              items={[
                { label: "Sent", value: money(transfers.sentTotal) },
                { label: "Sends", value: transfers.sentCount ?? 0 },
                { label: "Received", value: money(transfers.receivedTotal) },
                { label: "Receipts", value: transfers.receivedCount ?? 0 },
              ]}
            />
          </div>

          <section className="panel level-panel">
            <h2 className="section-title">Packages bought</h2>
            <div className="table-wrap">
              <table className="wide">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Code</th>
                    <th className="num">Amount</th>
                    <th>Status</th>
                    <th className="num">Daily</th>
                    <th className="num">Days</th>
                    <th className="num">Credited days</th>
                    <th className="num">Earned</th>
                    <th className="num">Left</th>
                    <th className="num">Plan</th>
                    <th>Start</th>
                  </tr>
                </thead>
                <tbody>
                  {packageRows.length ? packageRows.map((row) => (
                    <tr key={row.id}>
                      <td><Link to={"/admin/purchases/" + row.id}>{row.packageName}</Link></td>
                      <td className="code">{text(row.packageCode)}</td>
                      <td className="num">{money(row.amount)}</td>
                      <td><Badge value={row.status} /></td>
                      <td className="num">{money(row.dailyEarning)}</td>
                      <td className="num">{row.durationDays ?? 0}</td>
                      <td className="num">{row.creditedDays ?? 0}</td>
                      <td className="num">{money(row.earnedAmount)}</td>
                      <td className="num">{money(row.pendingAmount)}</td>
                      <td className="num">{money(row.expectedTotal)}</td>
                      <td>{when(row.startDate)}</td>
                    </tr>
                  )) : <EmptyRow cols={11} text="No packages" />}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel level-panel">
            <h2 className="section-title">Sent</h2>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th className="num">Amount</th><th>Note</th><th>Sent to</th><th>Time</th></tr>
                </thead>
                <tbody>
                  {sent.length ? sent.map((row) => (
                    <tr key={row.id}>
                      <td className="num">{money(row.amount)}</td>
                      <td>{text(row.note)}</td>
                      <td><Party person={row.recipient} /></td>
                      <td>{when(row.createdAt)}</td>
                    </tr>
                  )) : <EmptyRow cols={4} text="No sends" />}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel level-panel">
            <h2 className="section-title">Received</h2>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th className="num">Amount</th><th>Note</th><th>From</th><th>Time</th></tr>
                </thead>
                <tbody>
                  {received.length ? received.map((row) => (
                    <tr key={row.id}>
                      <td className="num">{money(row.amount)}</td>
                      <td>{text(row.note)}</td>
                      <td><Party person={row.sender} /></td>
                      <td>{when(row.createdAt)}</td>
                    </tr>
                  )) : <EmptyRow cols={4} text="No receipts" />}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel level-panel">
            <h2 className="section-title">Activity</h2>
            <p className="hint">Wallet statement, newest first. Only this list is paged.</p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Type</th><th>Direction</th><th className="num">Amount</th><th>Note</th><th>Time</th></tr>
                </thead>
                <tbody>
                  {activity.length ? activity.map((row) => (
                    <tr key={row.id}>
                      <td>{label(row.type)}</td>
                      <td><Badge value={row.direction} /></td>
                      <SignedAmount value={row.amount} direction={row.direction} />
                      <td>{text(row.note)}</td>
                      <td>{when(row.createdAt)}</td>
                    </tr>
                  )) : <EmptyRow cols={5} text="No activity" />}
                </tbody>
              </table>
            </div>
            {result.data ? (
              <Pager
                page={page}
                limit={ACTIVITY_LIMIT}
                total={result.data.total}
                onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })}
              />
            ) : null}
          </section>
        </>
      ) : null}
    </Page>
  );
}
